import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { label: t('nav_about'), href: '#about' },
    { label: t('nav_products'), href: '#products' },
    { label: t('nav_features'), href: '#features' },
    { label: t('nav_contact'), href: '#contact' },
  ];

  const handleNav = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  const toggleLang = () => {
    const next = i18n.language === 'ko' ? 'en' : 'ko';
    i18n.changeLanguage(next);
  };

  const isKo = i18n.language === 'ko';

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8 h-20 md:h-32 flex items-center justify-between">
        {/* Logo */}
        <a href="#" className="flex items-center gap-2 md:gap-4 cursor-pointer">
          <div className="w-12 h-12 md:w-24 md:h-24 flex items-center justify-center rounded-lg overflow-hidden shrink-0">
            <img
              src="/images/logo.jpeg"
              alt="시너지스틸 CI"
              className="w-full h-full object-contain"
            />
          </div>
          <div>
            <div className={`text-2xl md:text-4xl font-black tracking-tight leading-none ${scrolled ? 'text-[#1C2E4A]' : 'text-white'}`}>
              시너지스틸
            </div>
            <div className={`text-sm md:text-2xl tracking-widest mt-0.5 md:mt-1 ${scrolled ? 'text-[#E8762B]' : 'text-[#E8762B]'}`}>
              SYNERGY STEEL
            </div>
          </div>
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-10">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              onClick={(e) => handleNav(e, item.href)}
              className={`text-[28px] font-medium transition-colors cursor-pointer whitespace-nowrap hover:text-[#E8762B] ${
                scrolled ? 'text-[#1C2E4A]' : 'text-white'
              }`}
            >
              {item.label}
            </a>
          ))}
        </div>

        {/* Right Actions */}
        <div className="hidden md:flex items-center gap-3">
          {/* Language Toggle */}
          <button
            onClick={toggleLang}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full border text-xs font-bold tracking-wider transition-all cursor-pointer whitespace-nowrap ${
              scrolled
                ? 'border-[#1C2E4A]/30 text-[#1C2E4A] hover:border-[#E8762B] hover:text-[#E8762B]'
                : 'border-white/40 text-white hover:border-[#E8762B] hover:text-[#E8762B]'
            }`}
            aria-label="Switch language"
          >
            <span className={isKo ? 'opacity-100 font-black' : 'opacity-40'}>KO</span>
            <span className={scrolled ? 'text-[#1C2E4A]/30' : 'text-white/30'}>|</span>
            <span className={!isKo ? 'opacity-100 font-black' : 'opacity-40'}>EN</span>
          </button>

          {/* CTA Button */}
          <a
            href="#contact"
            onClick={(e) => handleNav(e, '#contact')}
            className="flex items-center gap-2 bg-[#E8762B] text-white px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-[#D4691E] transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-send-line text-base"></i>
            {t('nav_cta')}
          </a>
        </div>

        {/* Mobile Right */}
        <div className="flex md:hidden items-center gap-2">
          {/* Mobile Language Toggle */}
          <button
            onClick={toggleLang}
            className={`flex items-center gap-1 px-2.5 py-1 rounded-full border text-[10px] font-bold tracking-wider transition-all cursor-pointer whitespace-nowrap ${
              scrolled
                ? 'border-[#1C2E4A]/30 text-[#1C2E4A]'
                : 'border-white/40 text-white'
            }`}
          >
            <span className={isKo ? 'opacity-100' : 'opacity-40'}>KO</span>
            <span className="opacity-30">|</span>
            <span className={!isKo ? 'opacity-100' : 'opacity-40'}>EN</span>
          </button>

          {/* Mobile Menu Toggle */}
          <button
            className={`text-3xl cursor-pointer p-1 ${scrolled ? 'text-[#1C2E4A]' : 'text-white'}`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={t('nav_menu_open')}
          >
            <i className={menuOpen ? 'ri-close-line' : 'ri-menu-3-line'}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-5 flex flex-col gap-4">
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              onClick={(e) => handleNav(e, item.href)}
              className="text-base font-medium text-[#1C2E4A] hover:text-[#E8762B] cursor-pointer whitespace-nowrap py-1"
            >
              {item.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={(e) => handleNav(e, '#contact')}
            className="bg-[#E8762B] text-white px-5 py-3 rounded-md text-sm font-semibold text-center cursor-pointer whitespace-nowrap mt-1"
          >
            {t('nav_cta')}
          </a>
        </div>
      )}
    </nav>
  );
}
